import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  Search,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Position, Account } from "@shared/schema";
import { useState } from "react";

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00%";
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function PositionTableRow({ position }: { position: Position }) {
  const unrealizedPL = parseFloat(position.unrealizedPL?.toString() || "0");
  const isPositive = unrealizedPL >= 0;

  return (
    <TableRow className="hover-elevate">
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">
              {position.symbol?.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium">{position.symbol}</p>
            <p className="text-xs text-muted-foreground truncate max-w-[150px]">{position.name}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {position.securityType || "Stock"}
        </Badge>
      </TableCell>
      <TableCell className="text-right font-mono">{position.quantity}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(position.averagePrice)}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(position.currentPrice)}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(position.marketValue)}</TableCell>
      <TableCell className="text-right">
        <div className={`flex items-center justify-end gap-1 ${isPositive ? "text-chart-1" : "text-destructive"}`}>
          {isPositive ? (
            <ArrowUpRight className="h-4 w-4" />
          ) : (
            <ArrowDownRight className="h-4 w-4" />
          )}
          <span className="font-mono">{formatCurrency(unrealizedPL)}</span>
        </div>
      </TableCell>
      <TableCell className={`text-right font-mono ${isPositive ? "text-chart-1" : "text-destructive"}`}>
        {formatPercent(position.unrealizedPLPercent)}
      </TableCell>
    </TableRow>
  );
}

export default function PortfolioPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: accounts, isLoading: accountsLoading } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
  });

  const { data: positions, isLoading: positionsLoading } = useQuery<Position[]>({
    queryKey: ["/api/positions"],
  });

  const totalPortfolioValue = accounts?.reduce(
    (sum, acc) => sum + parseFloat(acc.portfolioValue?.toString() || "0"),
    0
  ) || 0;

  const totalUnrealizedPL = positions?.reduce(
    (sum, pos) => sum + parseFloat(pos.unrealizedPL?.toString() || "0"),
    0
  ) || 0;

  const totalCostBasis = positions?.reduce(
    (sum, pos) => sum + parseFloat(pos.costBasis?.toString() || "0"),
    0
  ) || 0;

  const overallReturn = totalCostBasis > 0 ? (totalUnrealizedPL / totalCostBasis) * 100 : 0;

  const filteredPositions = positions?.filter(
    (pos) =>
      pos.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pos.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLoading = accountsLoading || positionsLoading;

  return (
    <MainLayout
      title="Portfolio"
      subtitle="Your investment positions"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="button-filter">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm" data-testid="button-export">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Value</p>
              {isLoading ? (
                <Skeleton className="h-7 w-32 mt-1" />
              ) : (
                <p className="text-2xl font-bold" data-testid="stat-total-value">
                  {formatCurrency(totalPortfolioValue)}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total P&L</p>
              {isLoading ? (
                <Skeleton className="h-7 w-32 mt-1" />
              ) : (
                <p
                  className={`text-2xl font-bold ${totalUnrealizedPL >= 0 ? "text-chart-1" : "text-destructive"}`}
                  data-testid="stat-total-pl"
                >
                  {totalUnrealizedPL >= 0 ? "+" : ""}
                  {formatCurrency(totalUnrealizedPL)}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Overall Return</p>
              {isLoading ? (
                <Skeleton className="h-7 w-20 mt-1" />
              ) : (
                <p
                  className={`text-2xl font-bold ${overallReturn >= 0 ? "text-chart-1" : "text-destructive"}`}
                  data-testid="stat-overall-return"
                >
                  {formatPercent(overallReturn)}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Positions</p>
              {isLoading ? (
                <Skeleton className="h-7 w-12 mt-1" />
              ) : (
                <p className="text-2xl font-bold" data-testid="stat-positions-count">
                  {positions?.length || 0}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-lg font-medium">Holdings</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search positions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-positions"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-9 w-9 rounded-md" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            ) : filteredPositions && filteredPositions.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Avg Price</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Market Value</TableHead>
                      <TableHead className="text-right">P&L</TableHead>
                      <TableHead className="text-right">Return</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPositions.map((position) => (
                      <PositionTableRow key={position.id} position={position} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No positions found</h3>
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : "Start trading to build your portfolio"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

import { Briefcase } from "lucide-react";
